import * as React from 'react';

import { fromJson } from '@bufbuild/protobuf';
import {
  OrganizationEvent,
  OrganizationEventSchema,
} from '@rematter/frontend-lib-api-client/grpc/core/v1/organization_events_pb';
import { Col, EventDetailContainer, JsonViewer } from '@rematter/ui-web';

import { OrganizationCreatedDetail } from './OrganizationCreatedDetail';
import { OrganizationDeletedDetail } from './OrganizationDeletedDetail';
import { OrganizationEntitlementGrantedDetail } from './OrganizationEntitlementGrantedDetail';
import { OrganizationEntitlementRevokedDetail } from './OrganizationEntitlementRevokedDetail';
import { OrganizationProvisionedDetail } from './OrganizationProvisionedDetail';
import { OrganizationSignupCompletedDetail } from './OrganizationSignupCompletedDetail';
import { OrganizationUpdatedDetail } from './OrganizationUpdatedDetail';

import { useTranslations } from '@/admin/i18n/useTranslations';

type OrganizationEventDetailProps = {
  payload: string;
};

export const OrganizationEventDetail = ({
  payload,
}: OrganizationEventDetailProps) => {
  const t = useTranslations();
  const [showJson, setShowJson] = React.useState(false);

  const event = React.useMemo((): OrganizationEvent | null => {
    try {
      return fromJson(OrganizationEventSchema, JSON.parse(payload));
    } catch {
      return null;
    }
  }, [payload]);

  if (!event) {
    return <JsonViewer data={payload} title={t.logs.rawPayloadParseError} />;
  }

  const { event: inner } = event;

  const renderEventContent = () => {
    switch (inner.case) {
      case 'created':
        return <OrganizationCreatedDetail event={inner.value} />;
      case 'updated':
        return <OrganizationUpdatedDetail event={inner.value} />;
      case 'deleted':
        return <OrganizationDeletedDetail event={inner.value} />;
      case 'provisioned':
        return <OrganizationProvisionedDetail event={inner.value} />;
      case 'entitlementGranted':
        return <OrganizationEntitlementGrantedDetail event={inner.value} />;
      case 'entitlementRevoked':
        return <OrganizationEntitlementRevokedDetail event={inner.value} />;
      case 'signupCompleted':
        return <OrganizationSignupCompletedDetail event={inner.value} />;
      case undefined:
        return <JsonViewer data={payload} title={t.logs.unknownEventType} />;
    }
  };

  const getEventTitle = () => {
    switch (inner.case) {
      case 'created':
        return t.logs.events.organizationCreated;
      case 'updated':
        return t.logs.events.organizationUpdated;
      case 'deleted':
        return t.logs.events.organizationDeleted;
      case 'provisioned':
        return t.logs.events.organizationProvisioned;
      case 'entitlementGranted':
        return t.logs.events.entitlementGranted;
      case 'entitlementRevoked':
        return t.logs.events.entitlementRevoked;
      case 'signupCompleted':
        return t.logs.events.signupCompleted;
      case undefined:
        return t.logs.unknownEvent;
    }
  };

  return (
    <Col className="@OrganizationEventDetail flex-1">
      <EventDetailContainer
        title={getEventTitle()}
        showJson={showJson}
        onToggleJson={() => setShowJson(!showJson)}
        jsonContent={payload}
      >
        {renderEventContent()}
      </EventDetailContainer>
    </Col>
  );
};
